import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import { withTranslation } from 'react-i18next';
import { getTByLang } from 'services/i18n';

import {
  ButtonsContainer,
  ButtonWithLoader,
  Col,
  RFGroupError,
  RFFormTextarea,
  Row,
  RFStatusItem,
  RFFormInput,
  RFFormSelect,
  InfoRow,
} from 'virtues-bootstrap';
import { Field, reduxForm } from 'redux-form';

import { FORM_NAMES } from 'constants/forms';

import validate from './validate';
import s from './styles.module.scss';

const DeviceStatusPicker = ({
  deviceStatusLang,
  patientInstructions,
  guidelineLang,
  change,
  pristine,
  handleSubmit,
  submitting,
  t,
  initialValues,
}) => {
  const deviceStatusTranslate = getTByLang(deviceStatusLang);
  const guidelineTranslate = getTByLang(guidelineLang);

  return (
    <form onSubmit={handleSubmit}>
      <div className={s.formWrapper}>
        <div className={s.title}>
          <div>{t('patient.deviceStatus')}</div>
          <div>
            <Field className={s.langSelect} component={RFFormSelect} name="deviceStatusLang">
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </Field>
          </div>
        </div>
        <Row>
          <Col xs={6}>
            <Field
              component={RFStatusItem}
              name="devicestatus"
              value="success"
              variant="success"
              type="radio"
              onClick={() => {
                change(
                  'patientInstructions',
                  `${deviceStatusTranslate(
                    'reports.status.deviceWorkingWell',
                  )} ${patientInstructions.split(';')[1] || initialValues.patientInstructions}`,
                );
              }}
            >
              {deviceStatusTranslate('reports.status.deviceWorkingWell')}
            </Field>
            <Field
              component={RFStatusItem}
              name="devicestatus"
              value="warning"
              variant="warning"
              type="radio"
              onChange={() =>
                change(
                  'patientInstructions',
                  `${deviceStatusTranslate('reports.status.contactClinicAtEarliest', {
                    phone:
                      initialValues.phone ||
                      deviceStatusTranslate('reports.numberNotListedInLighter'),
                  })} ${patientInstructions.split(';')[1] || initialValues.patientInstructions}`,
                )
              }
            >
              {deviceStatusTranslate('reports.status.contactClinicAtEarliest', {
                phone:
                  initialValues.phone || deviceStatusTranslate('reports.numberNotListedInLighter'),
              })}
            </Field>
            <Field
              component={RFStatusItem}
              name="devicestatus"
              value="danger"
              variant="danger"
              type="radio"
              onChange={() =>
                change(
                  'patientInstructions',
                  `${deviceStatusTranslate('reports.status.contactImmediately', {
                    phone:
                      initialValues.phone ||
                      deviceStatusTranslate('reports.numberNotListedInLighter'),
                  })} ${patientInstructions.split(';')[1] || initialValues.patientInstructions}`,
                )
              }
            >
              {deviceStatusTranslate('reports.status.contactImmediately', {
                phone:
                  initialValues.phone || deviceStatusTranslate('reports.numberNotListedInLighter'),
              })}
            </Field>
            <Field component={RFGroupError} name="devicestatus" />
          </Col>
          <Col xs={6}>
            <Field
              component={RFFormTextarea}
              label={`${t('reports.patientInstructionsRequired')}`}
              maxLength={1000}
              name="patientInstructions"
              rows={4}
            />
            <Field
              component={RFFormTextarea}
              label={`${t('reports.recommendationsForPhysicianRequired')}`}
              maxLength={1000}
              name="physicianRecommendations"
              rows={4}
            />
            {initialValues.phone ? (
              <Field
                component={RFFormInput}
                disabled
                label={t('reports.phoneNumber')}
                name="phone"
                rows={5}
              />
            ) : (
              <>
                <InfoRow
                  className={s.notListedPhone}
                  noHorizontalSpace
                  wrap
                  title={t('reports.phoneNumber')}
                />
                <div>{t('reports.numberNotListed')}</div>
              </>
            )}
          </Col>
        </Row>
      </div>

      <div className={s.formWrapper}>
        <div className={s.title}>
          <div>{t('reports.guidelineDirectedMedicalTherapy')}</div>
          <div>
            <Field
              className={s.langSelect}
              component={RFFormSelect}
              name="guidelineLang"
              onChange={() => change('therapy', '')}
            >
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </Field>
          </div>
        </div>
        <Row>
          <Col xs={6}>
            <Field
              component={RFStatusItem}
              name="therapy"
              value="notApplicable"
              type="radio"
              variant="notApplicable"
              onChange={() =>
                change('therapyText', guidelineTranslate('reports.medicalTherapy.notApplicable'))
              }
            >
              {guidelineTranslate('reports.medicalTherapy.notApplicable')}
            </Field>
            <Field
              component={RFStatusItem}
              name="therapy"
              value="success"
              variant="success"
              type="radio"
              onChange={() =>
                change('therapyText', guidelineTranslate('reports.medicalTherapy.optimal'))
              }
            >
              {guidelineTranslate('reports.medicalTherapy.optimal')}
            </Field>
            <Field
              component={RFStatusItem}
              name="therapy"
              value="danger"
              variant="danger"
              type="radio"
              onChange={() =>
                change('therapyText', guidelineTranslate('reports.medicalTherapy.notOptimal'))
              }
            >
              {guidelineTranslate('reports.medicalTherapy.notOptimal')}
            </Field>
            <Field component={RFGroupError} name="therapy" />
          </Col>
          <Col xs={6}>
            <Field
              component={RFFormTextarea}
              label={`${t('reports.commentsRequired')}`}
              maxLength={1000}
              name="comments"
              rows={5}
            />
          </Col>
        </Row>
        <ButtonsContainer>
          <ButtonWithLoader
            id="submitToPatientAndDeviceClinic"
            disabled={pristine}
            loading={submitting}
          >
            {t('reports.submitToPatientAndDeviceClinic')}
          </ButtonWithLoader>
        </ButtonsContainer>
      </div>
    </form>
  );
};

DeviceStatusPicker.propTypes = {
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  deviceStatusLang: PropTypes.string,
  guidelineLang: PropTypes.string,
  patientInstructions: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    phone: PropTypes.string,
    patientInstructions: PropTypes.string,
  }).isRequired,
};

DeviceStatusPicker.defaultProps = {
  deviceStatusLang: 'en',
  guidelineLang: 'en',
};

export default compose(
  withTranslation(),
  reduxForm({
    form: FORM_NAMES.deviceStatusPicker,
    validate,
  }),
)(DeviceStatusPicker);
